import { useCallback } from 'react';
import { CREATE, GET_ONE, showNotification, UPDATE } from 'react-admin';

const downloadPdf = (data, { fileName }) => {
  const newBlob = new Blob([data]);

  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  // For other browsers:
  // Create a link pointing to the ObjectURL containing the blob.
  const objectUrl = window.URL.createObjectURL(newBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();

  // For Firefox it is necessary to delay revoking the ObjectURL
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(data);
  }, 100);
};

export const useUnitInvoiceActions = ({ dataProvider, dispatch, isBusinessOwner }) => {
  const retryPayment = useCallback(
    async (invoiceId, data) => {
      try {
        await dataProvider(CREATE, 'businessUnitInvoices', {
          field: 'retryPayment',
          id: invoiceId,
          data,
          useBusinessClient: isBusinessOwner,
        });
        dispatch(showNotification('notification.invoices.setToPendingSuccessfully'));
      } catch (error) {
        console.error(`Failed to retry payment for invoice ${invoiceId}`, error);
        dispatch(
          showNotification('notification.invoices.setToPendingFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch, isBusinessOwner]
  );

  // Fetch the instant refund-note PDF as a blob for the in-app viewer (bearer auth can't ride
  // on an <iframe src>, so the viewer consumes this blob via URL.createObjectURL).
  const loadRefundNotePdf = useCallback(
    invoiceId =>
      dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', {
        field: 'refundNote',
        id: invoiceId,
        useBusinessClient: isBusinessOwner,
      }).then(({ data }) => new Blob([data], { type: 'application/pdf' })),
    [dataProvider, isBusinessOwner]
  );

  const downloadInvoicePdf = useCallback(
    invoiceId => {
      dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', {
        field: 'invoicePdf',
        id: invoiceId,
        useBusinessClient: isBusinessOwner,
      })
        .then(({ data }) => downloadPdf(data, { fileName: 'invoice' }))
        .catch(error => {
          console.error(`Failed to download invoice pdf for invoice ${invoiceId}`, error);
          dispatch(
            showNotification('notification.invoices.downloadInvoiceFailure', 'warning', {
              messageArgs: { error_message: error },
            })
          );
        });
    },
    [dataProvider, dispatch, isBusinessOwner]
  );

  const cancel = useCallback(
    async (invoiceId, data) => {
      try {
        await dataProvider(UPDATE, 'businessUnitInvoices', { field: 'cancel', id: invoiceId, data });
        dispatch(showNotification('notification.invoices.canceledSuccessfully'));
      } catch (error) {
        console.error(`Failed to cancel invoice ${invoiceId}`, error);
        dispatch(
          showNotification('notification.invoices.canceledFailure', 'warning', {
            messageArgs: { error_message: error },
          })
        );
      }
    },
    [dataProvider, dispatch]
  );

  const setAsPaid = useCallback(
    async (invoiceId, payload, data) => {
      try {
        await dataProvider(UPDATE, 'businessUnitInvoices', { field: 'setAsPaid', id: invoiceId, payload, data });
        dispatch(showNotification('notification.invoices.paidSuccessfully'));
      } catch (error) {
        console.error(`Failed to set invoice ${invoiceId} as paid`, error);
        dispatch(
          showNotification('notification.invoices.paidFailure', 'warning', { messageArgs: { error_message: error } })
        );
      }
    },
    [dataProvider, dispatch]
  );

  return {
    retryPayment,
    loadRefundNotePdf,
    downloadInvoicePdf,
    cancel,
    setAsPaid,
  };
};
